import { useAppInsightsContext, useTrackMetric } from '@microsoft/applicationinsights-react-js';
import React, { useCallback, useMemo } from 'react';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { SerializedError } from '@reduxjs/toolkit';
import { useAppSelector } from '../../ducks/hooks';
import AppEnvironment from '../../environment/app-environment';
import { EICMSeverity, EICMType } from '../../enums/icm-enum';
import serviceLogger from './service-logger';
import { EServiceName } from '../../ducks/api-ducks/types-shared';
import { useIsSinglePaylinePlan } from '../../utils/participation-incentive-type-hook';

enum EventType {
  Exception = 'EXCEPTION',
  Interaction = 'INTERACTION',
  Event = 'EVENT',
  Visit = 'VISIT',
}

interface IEventProperties {
  environment: string;
  isImpersonating?: boolean;
  impersonatedUser?: string;
  loggedUser?: string;
  businessPersonaRoleName?: string;
  isBusinessPersona?: boolean;
  businessSegment?: string;
  countryCode?: string;
  planName?: string;
  userRole?: string;
  pageName?: string;
  incentiveType?: string;
  isSinglePayline?: boolean;
  componentName?: string;
  eventType: EventType;
  customProps?: any;
}

export interface IICMProperties {
  type: EICMType;
  serviceName?: EServiceName;
  severity: EICMSeverity;
  responseCode: string;
  message: string;
  invalidDateValue?: string;
}

/**
 * Reusable hook that extracts common telemetry properties from Redux state.
 * This centralizes the logic for gathering user context, participation details,
 * and SPL detection to avoid duplication across telemetry hooks.
 */
export const useTelemetryProperties = () => {
  const { isBusinessPersona, businessPersonaRoleName } = useAppSelector((state) => state.appConfig);
  const { impersonatedUser, loggedUser, isImpersonating } = useAppSelector((state) => state.impersonation);
  const filters = useAppSelector((state) => state.filters);
  const participationDetails = useAppSelector((state) => state.participationDetails);
  const selectedParticipation = useMemo(
    () => participationDetails?.coreParticipations?.find((participation) => filters.participationId === participation.participationId),
    [filters.participationId, participationDetails?.coreParticipations]
  );
  const planName = selectedParticipation?.planName;
  const userRole = selectedParticipation?.userRole;
  const businessSegment = selectedParticipation?.businessSegmentName;
  const countryCode = selectedParticipation?.countryCode;
  const incentiveType = useMemo(() => selectedParticipation?.payMixDetails?.incentiveType, [selectedParticipation?.payMixDetails]);
  const isSinglePayline = useIsSinglePaylinePlan();

  return useMemo(
    () => ({
      impersonatedUser,
      loggedUser,
      isImpersonating,
      businessPersonaRoleName,
      isBusinessPersona,
      businessSegment,
      countryCode,
      planName,
      userRole,
      incentiveType,
      isSinglePayline,
    }),
    [
      impersonatedUser,
      loggedUser,
      isImpersonating,
      businessPersonaRoleName,
      isBusinessPersona,
      businessSegment,
      countryCode,
      planName,
      userRole,
      incentiveType,
      isSinglePayline,
    ]
  );
};

export const useTrackPageEngagement = (componentName: string) => {
  const appInsights = useAppInsightsContext();
  const customProps = useTelemetryProperties();
  const tracker = useTrackMetric(appInsights, componentName, customProps);

  React.useEffect(() => {
    appInsights.trackPageView({ name: componentName, properties: customProps });
  }, [appInsights, componentName, customProps]);

  return tracker;
};

export const useTrackInteraction = (): ((
  description: string,
  pageName: string,
  componentName: string,
  customProps?: { [key: string]: any }
) => void) => {
  const appInsights = useAppInsightsContext();
  const telemetryProps = useTelemetryProperties();

  return useCallback(
    (description: string, pageName: string, componentName: string, customProps?: { [key: string]: any }) => {
      const properties: IEventProperties = {
        ...telemetryProps,
        environment: `${AppEnvironment.env}`,
        componentName,
        eventType: EventType.Interaction,
        pageName,
      };

      if (telemetryProps.impersonatedUser !== '') {
        properties.impersonatedUser = telemetryProps.impersonatedUser;
      }

      if (customProps) {
        properties.customProps = customProps;
      }

      const name = `${pageName}-${componentName}-${description}`;

      appInsights.trackEvent({
        name,
        properties,
      });
    },
    [appInsights, telemetryProps]
  );
};

export const useTrackTrace = (): ((description: string, pageName: string, componentName?: string, customProps?: { [key: string]: any }) => void) => {
  const appInsights = useAppInsightsContext();
  const telemetryProps = useTelemetryProperties();

  return useCallback(
    (description: string, pageName: string, componentName?: string, customProps?: { [key: string]: any }) => {
      const properties: IEventProperties = {
        ...telemetryProps,
        environment: `${AppEnvironment.env}`,
        eventType: EventType.Event,
        pageName,
      };

      if (pageName) {
        properties.pageName = pageName;
      }

      if (componentName) {
        properties.componentName = componentName;
      }

      if (telemetryProps.impersonatedUser !== '') {
        properties.impersonatedUser = telemetryProps.impersonatedUser;
      }

      if (customProps) {
        properties.customProps = customProps;
      }

      appInsights.trackTrace({
        message: description,
        properties,
      });
    },
    [appInsights, telemetryProps]
  );
};

export const useTrackEvent = (): ((description: string, pageName: string, componentName?: string, customProps?: { [key: string]: any }) => void) => {
  const appInsights = useAppInsightsContext();
  const telemetryProps = useTelemetryProperties();

  return useCallback(
    (description: string, pageName: string, componentName?: string, customProps?: { [key: string]: any }) => {
      const properties: IEventProperties = {
        ...telemetryProps,
        environment: `${AppEnvironment.env}`,
        eventType: EventType.Event,
        pageName,
      };

      if (componentName) {
        properties.componentName = componentName;
      }

      if (telemetryProps.impersonatedUser !== '') {
        properties.impersonatedUser = telemetryProps.impersonatedUser;
      }

      if (customProps) {
        properties.customProps = customProps;
      }

      const name = `${pageName}-${componentName}-${description}`;

      appInsights.trackEvent({
        name,
        properties,
      });
    },
    [appInsights, telemetryProps]
  );
};

export const useTrackException = (): ((error: string, pageName: string, componentName?: string, customProps?: any) => void) => {
  const { appInsights } = serviceLogger;
  const telemetryProps = useTelemetryProperties();

  return useCallback(
    (error: string, pageName: string, componentName?: string, customProps?: any) => {
      const properties: IEventProperties = {
        ...telemetryProps,
        environment: `${AppEnvironment.env}`,
        eventType: EventType.Exception,
        pageName,
      };

      if (appInsights) {
        if (componentName) {
          properties.componentName = componentName;
        }

        if (telemetryProps.impersonatedUser !== '') {
          properties.impersonatedUser = telemetryProps.impersonatedUser;
        }

        if (customProps) {
          properties.customProps = customProps;
        }

        appInsights.trackException({
          exception: new Error(error),
          properties,
        });
      }
    },
    [appInsights, telemetryProps]
  );
};

export const useTrackICMEvent = (): ((error: string, pageName: string, componentName?: string, icmProps?: IICMProperties) => void) => {
  const trackException = useTrackException();
  const trackTrace = useTrackTrace();

  return useCallback(
    (error: string, pageName: string, componentName?: string, icmProps?: IICMProperties) => {
      if (isAuthRelatedError(icmProps)) {
        const eventName: string = `<Informational> <${icmProps?.type}>${icmProps?.serviceName ? ` <${icmProps?.serviceName}>` : ''} ${error}`;
        trackTrace(eventName, pageName, componentName, icmProps);
      } else {
        const eventName: string = `<MEM ICM> <${icmProps?.type}>${icmProps?.serviceName ? ` <${icmProps?.serviceName}>` : ''} ${error}`;
        trackException(eventName, pageName, componentName, icmProps);
      }
    },
    [trackException, trackTrace]
  );
};

export const useBuildICMProperties = () =>
  useCallback((type: EICMType, severity: EICMSeverity, serviceName?: EServiceName, error?: FetchBaseQueryError | SerializedError): IICMProperties => {
    let responseCode: string = '';
    let message: string = '';

    if (error) {
      if ('status' in error) {
        // you can access all properties of `FetchBaseQueryError` here
        responseCode = error.status.toString();
        message = `${'error' in error ? error.error : ''} ${error.data ? JSON.stringify(error.data) : ''}`;
      } else {
        // you can access all properties of `SerializedError` here
        responseCode = error.code ? error.code : '';
        message = error.message ? error.message : '';
      }
    }

    return {
      type,
      serviceName,
      severity,
      responseCode,
      message,
    };
  }, []);

// DO NOT USE!! This one still here while we migrate our file downloads to be hook based.
export const buildICMProperties = (
  type: EICMType,
  severity: EICMSeverity,
  serviceName?: EServiceName,
  error?: FetchBaseQueryError | SerializedError
): IICMProperties => {
  let responseCode: string = '';
  let message: string = '';

  if (error) {
    if ('status' in error) {
      // you can access all properties of `FetchBaseQueryError` here
      responseCode = error.status.toString();
      message = `${'error' in error ? error.error : ''} ${error.data ? JSON.stringify(error.data) : ''}`;
    } else {
      // you can access all properties of `SerializedError` here
      responseCode = error.code ? error.code : '';
      message = error.message ? error.message : '';
    }
  }

  return {
    type,
    serviceName,
    severity,
    responseCode,
    message,
  };
};

export const logTraceToAppInsights = (description: string, pageName: string, componentName: string, impersonatedUser: string, customProps?: any) => {
  const { appInsights } = serviceLogger;

  if (appInsights) {
    const properties: IEventProperties = {
      environment: `${AppEnvironment.env}`,
      eventType: EventType.Event,
      impersonatedUser,
      pageName,
    };

    if (componentName) {
      properties.componentName = componentName;
    }

    if (impersonatedUser !== '') {
      properties.impersonatedUser = impersonatedUser;
    }

    if (customProps) {
      properties.customProps = customProps;
    }

    appInsights.trackTrace({
      message: description,
      properties,
    });
  }
};

export const logExceptionToAppInsights = (error: string, pageName: string, componentName: string, impersonatedUser: string, customProps?: any) => {
  const { appInsights } = serviceLogger;

  if (appInsights) {
    const properties: IEventProperties = {
      environment: `${AppEnvironment.env}`,
      eventType: EventType.Event,
      pageName,
    };

    if (componentName) {
      properties.componentName = componentName;
    }

    if (impersonatedUser !== '') {
      properties.impersonatedUser = impersonatedUser;
    }

    if (customProps) {
      properties.customProps = customProps;
    }

    appInsights.trackException({
      exception: new Error(error),
      properties,
    });
  }
};

// Helper to detect auth-related errors that should be logged as informational traces
// rather than exceptions, to avoid triggering alerts for known MSX stale session issues.
// Conditions are intentionally narrow — response codes are paired with message checks
// to avoid suppressing genuine 401s, network errors, or authorization regressions.
const isAuthRelatedError = (icmProps?: IICMProperties): boolean =>
  icmProps?.responseCode === 'FETCH_ERROR' ||
  (icmProps?.responseCode === '401' && (icmProps?.message?.includes('Token is not a valid JWT') ?? false)) ||
  (icmProps?.message?.includes('redirect_bridge_timeout') ?? false) ||
  (icmProps?.message?.includes('Token is not a valid JWT') ?? false) ||
  (icmProps?.message?.includes('no_account_error') ?? false) ||
  (icmProps?.message?.includes('No token found') ?? false) ||
  (icmProps?.message?.includes('timed_out') ?? false) ||
  (icmProps?.message?.includes('no_network_connectivity') ?? false) ||
  (icmProps?.message?.includes('popup_window_error') ?? false) ||
  (icmProps?.message?.includes('empty_window_error') ?? false) ||
  (icmProps?.message?.includes('Blocked popup exception caught in acquireToken') ?? false) ||
  (icmProps?.message?.includes('interaction_in_progress') ?? false) ||
  (icmProps?.message?.includes('post_request_failed') ?? false) ||
  (icmProps?.message?.includes('untrusted_authority') ?? false) ||
  (icmProps?.message?.includes('user_cancelled') ?? false);

export const trackICMEvent = (error: string, pageName: string, componentName?: string, icmProps?: IICMProperties) => {
  if (isAuthRelatedError(icmProps)) {
    const eventName: string = `<Informational> <${icmProps?.type}>${icmProps?.serviceName ? ` <${icmProps?.serviceName}>` : ''} ${error}`;
    logTraceToAppInsights(eventName, pageName, componentName || '', '', icmProps);
  } else {
    const eventName: string = `<MEM ICM> <${icmProps?.type}>${icmProps?.serviceName ? ` <${icmProps?.serviceName}>` : ''} ${error}`;
    logExceptionToAppInsights(eventName, pageName, componentName || '', '', icmProps);
  }
};
